import { type FindexAsset } from '../../types/models/asset'
import {
  EAssetType,
  EAssetSource,
  EAssetSubtype,
  EAssetIndustry,
  type IAsset,
  getAssetMaintainedType,
  EAssetMaintainer,
  type ICommodityQuote,
  type ICryptoQuote,
  type FullAsset,
  type FindexInvestment
} from 'findef'
import { toString } from '../formatting'
import { EAdminRole } from 'findef/build/module/findef/assetAdmin'

export const getAssetType = (asset: FindexAsset): EAssetType => {
  if (typeof asset.type !== 'undefined') return asset.type
  return EAssetType.ALTERNATIVE
}

export const getAssetSource = (asset: FindexAsset | FullAsset): EAssetSource => {
  return asset.source || EAssetSource.MANUAL
}

export const getAssetId = (asset: FindexAsset): string => {
  return asset._id
}

export const getAssetChildIds = (asset: FindexAsset): string[] => {
  return (asset.childrenIds || []).map(toString)
}

// Removed since it is used in unused method transformInvestment in /utils/investment
// export const transformAsset = (asset: FindexAsset | ICoreAsset): FindexAsset => {
//   const name = isCoreAsset(asset) ? asset.name : asset.name
//   const id = isCoreAsset(asset) ? asset.id : asset._id
//   const childrenIds = asset.childrenIds || []
//   const parentId = asset.parentId
//   const organizationNumber = asset.organizationNumber
//   const contactEmail = isCoreAsset(asset) ? '' : asset.contactEmail
//   const updatedAt = parseDate(isCoreAsset(asset) ? asset.lastUpdated : asset.updatedAt)
//   const createdAt = isCoreAsset(asset) ? updatedAt : asset.createdAt

//   const ledger = isCoreAsset(asset)
//     ? ({
//         sharePrice: { value: 0, type: 'SEK' },
//         sharesIssued: 0
//       } as ILedger)
//     : asset.ledger

//   return {
//     _id: id,
//     name,
//     organizationNumber: (organizationNumber || '').toString(),
//     contactEmail,
//     childrenIds,
//     parentId,
//     updatedAt,
//     createdAt,
//     ledger
//   }
// }

export const getAssetShareCount = (asset: FindexAsset): number => {
  return asset.ledger.sharesIssued
}

export const getAssetAssignedSharesCount = (asset: FindexAsset, shareholders: any[]): number => {
  if (!shareholders || shareholders.length === 0) {
    return 0
  }
  return shareholders.reduce((acc, shareholder) => {
    const investmentForAsset = shareholder.investments?.find(
      (inv: FindexInvestment) => inv.asset._id === asset._id
    )
    const shares = investmentForAsset ? investmentForAsset.quantity : 0
    return acc + shares
  }, 0)
}

export const getAssetSharePrice = (asset: FindexAsset): number => {
  return asset.ledger?.sharePrice?.value || 0
}

export const getAssetSharePriceCurrency = (asset: FindexAsset): string | undefined => {
  return asset.ledger?.sharePrice.type
}

export const prettyAssetType = (
  t?: EAssetType,
  listed: boolean | undefined = false,
  subtypes: EAssetSubtype[] | undefined = undefined,
  plural: boolean = true,
  capitalLetters: boolean = false
) => {
  let text = ''
  switch (t) {
    case EAssetType.CASH:
      text = 'cash';
      break;
    case EAssetType.DEBT:
      return 'Debt';
    case EAssetType.EQUITY:
      text = listed
        ? plural
          ? 'listed equities'
          : 'listed equity'
        : plural
          ? 'unlisted equities'
          : 'unlisted equity'
      break
    case EAssetType.REAL_ESTATE:
      text = plural ? 'real estate' : 'real estate'
      break
    case EAssetType.CRYPTO:
      text = plural ? 'crypto' : 'crypto'
      break
    case EAssetType.COMMODITY:
      text = plural ? 'commodities' : 'commodity'
      break
    case EAssetType.ALTERNATIVE:
      if (subtypes?.includes(EAssetSubtype.LOAN)) {
        text = 'Debt'
        break
      }
      text = plural ? 'alternatives' : 'alternative'
      break
    default:
    case EAssetType.UNDEFINED:
      text = t + '' || (plural ? 'undefined' : 'undefined')
      break
  }

  if (capitalLetters) {
    text = text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  if (
    t !== EAssetType.CRYPTO &&
    t !== EAssetType.COMMODITY &&
    subtypes &&
    subtypes.length > 0 &&
    !subtypes?.includes(EAssetSubtype.LOAN)
  ) {
    let filteredSubtypes =
      subtypes.length > 1
        ? subtypes.filter(
            (sub) =>
              [
                EAssetSubtype.SAVINGS_ACCOUNT,
                EAssetSubtype.INVESTMENT_ACCOUNT,
                EAssetSubtype.CHECKING_ACCOUNT,
                EAssetSubtype.LOAN,
                EAssetSubtype.PENSION
              ].includes(sub) === false
          )
        : subtypes
    if (filteredSubtypes.includes(EAssetSubtype.CASH)) filteredSubtypes = [EAssetSubtype.CASH]

    const prettySubtypes = filteredSubtypes
      .map((subtype) => prettyAssetSubtype(subtype, capitalLetters))
      .join(', ')
    text = `${text} (${prettySubtypes})`
  }

  return text
}

export const prettyAssetSource = (source: EAssetSource) => {
  switch (source) {
    case EAssetSource.AUTOMATIC:
      return 'Provider'
    case EAssetSource.IR:
      return 'I. Relations'
    default:
      return 'Manual'
  }
}

export const getPrettyAssetSource = (asset: Partial<IAsset>): string => {
  if (asset.commodityQuote && typeof asset.commodityQuote === 'object') {
    const commodity = asset.commodityQuote as ICommodityQuote;
    if (commodity.manuallyAdded) return 'Manual';
  }
  if (asset.cryptoQuote && typeof asset.cryptoQuote === 'object') {
    const crypto = asset.cryptoQuote as ICryptoQuote;
    if (crypto.manuallyAdded) return 'Manual';
  }
  if (asset.type === EAssetType.COMMODITY) return 'Ticker';
  const maintained = getAssetMaintainedType(asset as IAsset);
  switch (maintained) {
    case EAssetMaintainer.IR: return 'I. Relations';
    case EAssetMaintainer.PROVIDER: return 'Provider';
    case EAssetMaintainer.TICKER: return 'Ticker';
    default:
    case EAssetMaintainer.MANUAL: return 'Manual';
  }
}

export const getPrettyAssetMaintained = (asset: Partial<IAsset>): string => {
  const maintained = getAssetMaintainedType(asset as IAsset);
  switch (maintained) {
    case EAssetMaintainer.IR: return 'I. Relations';
    case EAssetMaintainer.PROVIDER: return 'Provider';
    case EAssetMaintainer.MANUAL: return 'Manual';
    case EAssetMaintainer.TICKER: return 'Ticker';
    default: return getPrettyAssetSource(asset);
  }
}

export const prettyAssetSubtype = (t: EAssetSubtype, capitalLetters: boolean = false): string => {
  let text = ''
  switch (t) {
    case EAssetSubtype.STOCK:
    case EAssetSubtype.FUND:
    case EAssetSubtype.BOND:
    case EAssetSubtype.PENSION:
    case EAssetSubtype.APARTMENT:
    case EAssetSubtype.HOUSE:
    case EAssetSubtype.BUILDING:
    case EAssetSubtype.PARKING:
    case EAssetSubtype.COMMERCIAL:
    case EAssetSubtype.CRYPTO:
    case EAssetSubtype.COMMODITY:
    case EAssetSubtype.WATCH:
    case EAssetSubtype.JEWELLRY:
    case EAssetSubtype.GEMSTONE:
    case EAssetSubtype.LAND:
    case EAssetSubtype.CAR:
    case EAssetSubtype.ART:
    case EAssetSubtype.WINE:
    case EAssetSubtype.SNEAKERS:
    case EAssetSubtype.COLLECTIBLE:
    case EAssetSubtype.OTHER:
      text = t.toLowerCase()
      break
    case EAssetSubtype.FOREST_INVESTMENT:
    case EAssetSubtype.PRIVATE_DEBT:
    case EAssetSubtype.PRIVATE_EQUITY:
    case EAssetSubtype.HEDGE_FUND:
    case EAssetSubtype.SAVINGS_ACCOUNT:
    case EAssetSubtype.INVESTMENT_ACCOUNT:
    case EAssetSubtype.CHECKING_ACCOUNT:
    case EAssetSubtype.NO_ACCOUNT:
      text = t.toLowerCase().replace('_', ' ')
      break
    case EAssetSubtype.ETF:
      return EAssetSubtype.ETF
    case EAssetSubtype.NFT:
      return EAssetSubtype.NFT
    default:
      text = (t || '').toLowerCase()
      break
  }
  if (capitalLetters) {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  return text
}


export const prettyAssetSubtypes = (t: EAssetSubtype[], capitalLetters: boolean = false): string => {
  return (t || []).map(it => prettyAssetSubtype(it, capitalLetters)).join(',');
}

export const prettyAssetIndustry = (t: EAssetIndustry, capitalLetters: boolean = false) => {
  const text = t.toLowerCase().replace('_', ' ')
  if (capitalLetters) {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  return text
}

export const prettyAssetRole = (t: EAdminRole, capitalLetters: boolean = false) => {
  const text = t.toLowerCase().replace('_', ' ')
  if (capitalLetters) {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  return text
}

export const getPrettyAssetUnit = (type?: EAssetType, plural: boolean = false): string => {
  switch (type) {
    case EAssetType.CRYPTO:
      return plural ? 'Coins' : 'Coin'
    case EAssetType.REAL_ESTATE:
    case EAssetType.CASH:
    case EAssetType.COMMODITY:
    case EAssetType.ALTERNATIVE:
      return plural ? 'Units' : 'Unit'
    default:
      return plural ? 'Shares' : 'Share'
  }
}

export const formatOrgNbr = (orgNbr: string): string => {
  if (!orgNbr) return ''
  if (orgNbr.length !== 10) {
    return orgNbr
  }
  return `${orgNbr.slice(0, 6)}-${orgNbr.slice(6)}`
}

export const getAssetUnitPriceLabel = (type?: EAssetType): string => {
  switch (type) {
    case EAssetType.CRYPTO:
      return 'Coin Price'
    case EAssetType.CASH:
      return 'Value'
    case EAssetType.REAL_ESTATE:
    case EAssetType.COMMODITY:
    case EAssetType.ALTERNATIVE:
      return 'Unit Price'
    default:
      return 'Share Price'
  }
}

export const prettyAssetAddress = (asset: FindexAsset): string => {
  const { address, zip, city, country } = asset;

  const left = [address, zip].filter(Boolean).join(', ');
  const right = [city, country].filter(Boolean).join(', ');

  const result = [left, right].filter(Boolean).join(' | ');
  return result || 'Unknown location';
};
